
import { useState } from 'react';
import { toast } from 'sonner';
import { getAvailableWarehouses } from '@/services/search/optimizedInventorySearch';

export function useWarehouseSelection() {
  const [availableWarehouses, setAvailableWarehouses] = useState<{ id: number; name: string }[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Optimized warehouse loading using the optimized search service
  const loadWarehouses = async () => {
    try {
      setIsLoading(true);
      const warehouses = await getAvailableWarehouses();
      console.log('📦 Available warehouses loaded:', warehouses);
      
      if (warehouses.length === 0) {
        toast.warning('Geen magazijnen gevonden in de database');
      } else {
        toast.success(`${warehouses.length} magazijnen gevonden`);
      }
      
      setAvailableWarehouses(warehouses);
      setIsLoading(false);
      return warehouses;
    } catch (error) {
      console.error('Error loading warehouses:', error);
      // Fallback to standard warehouses
      const fallbackWarehouses = [
        { id: 2, name: 'Magazijn 2' },
        { id: 5, name: 'Magazijn 5' },
        { id: 7, name: 'Magazijn 7' },
        { id: 12, name: 'Magazijn 12' }
      ];
      setAvailableWarehouses(fallbackWarehouses);
      setIsLoading(false);
      return fallbackWarehouses;
    }
  };

  // Function to handle warehouse selection - fixed to ensure type consistency
  const selectWarehouse = (warehouseNumber: number | null) => {
    console.log('Setting warehouse filter to:', warehouseNumber);
    
    // Ensure we're using a number type (or null) consistently
    if (warehouseNumber !== null) {
      // If it's a string, convert it to a number
      const parsedWarehouse = typeof warehouseNumber === 'string' 
        ? parseInt(warehouseNumber, 10) 
        : warehouseNumber;
        
      if (isNaN(parsedWarehouse)) {
        console.error('Invalid warehouse number:', warehouseNumber);
        setSelectedWarehouse(null);
      } else {
        setSelectedWarehouse(parsedWarehouse);
      }
    } else {
      setSelectedWarehouse(null);
    }
  };

  // Refresh function that returns void for compatibility
  const refreshWarehouses = async (): Promise<void> => {
    await loadWarehouses();
  };

  return {
    availableWarehouses,
    selectedWarehouse,
    selectWarehouse,
    loadWarehouses,
    refreshWarehouses,
    isLoading
  };
}
